import { Truck, GraduationCap, Heart, DollarSign, Users, MapPin, CheckCircle, FileText, Upload, UserCheck, ArrowRight, Star } from 'lucide-react'
import SEOHead from '../components/SEOHead'

export default function Candidates() {
  const jobCategories = [
    {
      icon: <Truck className="h-12 w-12 text-c9-orange" />,
      title: "HGV/LGV Drivers",
      description: "Class 1 & Class 2 driving opportunities across the UK",
      opportunities: [
        "Local delivery drivers",
        "Long distance haulage", 
        "Multi-drop specialists",
        "Tanker drivers",
        "Crane operators"
      ],
      salaryRange: "£25,000 - £45,000"
    },
    {
      icon: <Users className="h-12 w-12 text-c9-orange" />,
      title: "PSV Drivers",
      description: "Passenger service vehicle driving roles",
      opportunities: [
        "Bus drivers",
        "Coach drivers",
        "School transport",
        "Tour operators",
        "Airport transfers"
      ],
      salaryRange: "£22,000 - £35,000"
    },
    {
      icon: <GraduationCap className="h-12 w-12 text-c9-orange" />,
      title: "Engineering & Technical",
      description: "Technical roles in transport and logistics",
      opportunities: [
        "Vehicle technicians",
        "Fleet managers",
        "Transport planners",
        "Operations supervisors",
        "Quality controllers"
      ],
      salaryRange: "£28,000 - £55,000"
    },
    {
      icon: <MapPin className="h-12 w-12 text-c9-orange" />,
      title: "Van Drivers",
      description: "Light goods vehicle and delivery opportunities",
      opportunities: [
        "Parcel delivery",
        "Multi-drop rounds",
        "Same day couriers",
        "Medical deliveries",
        "Retail logistics"
      ],
      salaryRange: "£20,000 - £32,000"
    }
  ]

  const benefits = [
    {
      icon: <DollarSign className="h-6 w-6 text-c9-orange" />,
      title: "Competitive Pay",
      description: "Above-market rates with weekly payments and overtime opportunities"
    },
    {
      icon: <Heart className="h-6 w-6 text-c9-orange" />,
      title: "Career Support", 
      description: "Ongoing training, development opportunities, and career progression guidance"
    },
    {
      icon: <Users className="h-6 w-6 text-c9-orange" />,
      title: "Dedicated Support",
      description: "Personal consultants available 24/7 for advice and assistance"
    },
    {
      icon: <CheckCircle className="h-6 w-6 text-c9-orange" />,
      title: "Flexible Working",
      description: "Temporary, permanent, and contract options to suit your lifestyle"
    }
  ]

  const applicationSteps = [
    {
      step: "1",
      icon: <FileText className="h-8 w-8 text-c9-orange" />,
      title: "Application",
      description: "Complete our simple online application form with your details and experience."
    },
    {
      step: "2", 
      icon: <UserCheck className="h-8 w-8 text-c9-orange" />,
      title: "Screening",
      description: "Our team reviews your application and conducts initial telephone interview."
    },
    {
      step: "3",
      icon: <Upload className="h-8 w-8 text-c9-orange" />,
      title: "Documentation",
      description: "Upload licenses, certificates, and references for compliance verification."
    },
    {
      step: "4",
      icon: <CheckCircle className="h-8 w-8 text-c9-orange" />,
      title: "Placement",
      description: "We match you with suitable opportunities and provide ongoing support."
    }
  ]

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Transport & Logistics Jobs - Find Your Next Role with C9 Recruitment"
        description="Discover HGV driving jobs, technical positions, and logistics careers across the UK. Weekly pay, career progression, and expert support from leading recruitment specialists."
        keywords="transport jobs, HGV driver jobs, logistics careers, driving jobs, technical roles, UK transport jobs, weekly pay, recruitment"
        canonical="/candidates"
      />

      {/* Floating Background Shapes */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="animate-float absolute top-20 left-10 w-20 h-20 bg-gradient-to-br from-c9-orange/20 to-c9-orange-alt/20 rounded-full blur-xl"></div>
        <div className="animate-float-delay-1 absolute top-1/3 right-20 w-32 h-32 bg-gradient-to-br from-c9-blue/20 to-c9-blue-alt/20 rounded-full blur-xl"></div>
        <div className="animate-float-delay-2 absolute bottom-1/4 left-1/4 w-24 h-24 bg-gradient-to-br from-c9-orange/20 to-c9-orange-alt/20 rounded-full blur-xl"></div>
        <div className="animate-float-delay-3 absolute bottom-20 right-1/3 w-28 h-28 bg-gradient-to-br from-c9-blue/20 to-c9-blue-alt/20 rounded-full blur-xl"></div>
      </div>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-c9-blue via-c9-blue-alt to-c9-blue text-white section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent"></div>
        <div className="container-full relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-8 animate-fade-in">
              <Star className="h-4 w-4 text-c9-orange" />
              <span>UK's Leading Transport Recruitment Specialists</span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent animate-fade-in-up">
              Drive Your <span className="bg-gradient-to-r from-c9-orange to-c9-orange-alt bg-clip-text text-transparent">Career Forward</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed animate-fade-in-up delay-100">
              Find your perfect transport and logistics role with competitive pay, career progression, and dedicated support from industry experts.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-200">
              <a 
                href="#job-categories" 
                className="btn-primary bg-gradient-to-r from-c9-orange to-c9-orange-alt hover:from-c9-orange-alt hover:to-c9-orange transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                View Job Categories
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
              <a 
                href="#apply" 
                className="btn-secondary bg-white/10 backdrop-blur-sm border-2 border-white/20 text-white hover:bg-white hover:text-c9-blue transform hover:scale-105 transition-all duration-300"
              >
                Apply Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Job Categories */}
      <section id="job-categories" className="section-padding bg-gradient-to-b from-gray-50 to-white">
        <div className="container-full">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-black text-c9-blue mb-6 animate-fade-in-up">
              Career <span className="bg-gradient-to-r from-c9-orange to-c9-orange-alt bg-clip-text text-transparent">Opportunities</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up delay-100">
              Discover rewarding positions across transport and logistics sectors with industry-leading companies.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {jobCategories.map((category, index) => (
              <div key={index} className="group bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden transform hover:-translate-y-2 animate-fade-in-up" style={{animationDelay: `${index * 100}ms`}}>
                <div className="p-8">
                  <div className="flex items-start gap-6">
                    <div className="bg-gradient-to-br from-c9-orange/10 to-c9-orange-alt/10 p-4 rounded-2xl group-hover:scale-110 transition-transform duration-300">
                      {category.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-c9-blue mb-3 group-hover:text-c9-orange transition-colors duration-300">
                        {category.title}
                      </h3>
                      <p className="text-gray-600 mb-4 leading-relaxed">
                        {category.description}
                      </p>
                      <div className="text-lg font-semibold text-c9-orange mb-4">
                        {category.salaryRange}
                      </div>
                    </div>
                  </div>
                  <div className="mt-6">
                    <h4 className="font-semibold text-c9-blue mb-3">Available Roles:</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {category.opportunities.map((opp, oppIndex) => (
                        <div key={oppIndex} className="flex items-center gap-2 text-gray-600">
                          <CheckCircle className="h-4 w-4 text-c9-orange flex-shrink-0" />
                          <span className="text-sm">{opp}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="h-1 bg-gradient-to-r from-c9-orange to-c9-orange-alt transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-gradient-to-br from-c9-blue to-c9-blue-alt text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent"></div>
        <div className="container-full relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-black mb-6 animate-fade-in-up">
              Why Choose <span className="bg-gradient-to-r from-c9-orange to-c9-orange-alt bg-clip-text text-transparent">C9 Recruitment</span>
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto animate-fade-in-up delay-100">
              We're committed to helping you succeed with comprehensive support and unmatched opportunities.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="group text-center animate-fade-in-up" style={{animationDelay: `${index * 100}ms`}}>
                <div className="bg-white/10 backdrop-blur-sm w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:bg-white/20 transition-all duration-300">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold mb-4 group-hover:text-c9-orange transition-colors duration-300">
                  {benefit.title}
                </h3>
                <p className="text-white/80 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="section-padding bg-gradient-to-b from-white to-gray-50">
        <div className="container-full">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-black text-c9-blue mb-6 animate-fade-in-up">
              Simple <span className="bg-gradient-to-r from-c9-orange to-c9-orange-alt bg-clip-text text-transparent">Application Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up delay-100">
              Get started with your new career in just four easy steps.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {applicationSteps.map((step, index) => (
              <div key={index} className="relative text-center group animate-fade-in-up" style={{animationDelay: `${index * 100}ms`}}>
                <div className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 p-8 transform hover:-translate-y-2">
                  <div className="relative mb-6">
                    <div className="bg-gradient-to-br from-c9-orange to-c9-orange-alt w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                      {step.icon}
                    </div>
                    <div className="absolute -top-2 -right-2 bg-c9-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">
                      {step.step}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-c9-blue mb-4 group-hover:text-c9-orange transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
                {index < applicationSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="h-6 w-6 text-c9-orange" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="section-padding bg-gradient-to-br from-gray-50 to-white">
        <div className="container-full">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-black text-c9-blue mb-6 animate-fade-in-up">
                Start Your <span className="bg-gradient-to-r from-c9-orange to-c9-orange-alt bg-clip-text text-transparent">Journey Today</span>
              </h2>
              <p className="text-xl text-gray-600 animate-fade-in-up delay-100">
                Complete the form below and one of our specialist consultants will be in touch within 24 hours.
              </p>
            </div>
            
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden animate-fade-in-up delay-200">
              <div className="bg-gradient-to-r from-c9-orange to-c9-orange-alt p-8 text-white text-center">
                <h3 className="text-2xl font-bold mb-2">Ready to Get Started?</h3>
                <p className="text-white/90">Let's find your perfect transport role</p>
              </div>
              <form className="p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <input 
                    type="text" 
                    placeholder="Full Name" 
                    className="w-full p-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-c9-orange focus:border-transparent transition-all duration-300"
                  />
                  <input 
                    type="email" 
                    placeholder="Email Address" 
                    className="w-full p-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-c9-orange focus:border-transparent transition-all duration-300"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <input 
                    type="tel" 
                    placeholder="Phone Number" 
                    className="w-full p-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-c9-orange focus:border-transparent transition-all duration-300"
                  />
                  <select className="w-full p-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-c9-orange focus:border-transparent transition-all duration-300">
                    <option>Select Career Interest</option>
                    <option>HGV/LGV Driver</option>
                    <option>PSV Driver</option>
                    <option>Van Driver</option>
                    <option>Engineering & Technical</option>
                    <option>Other</option>
                  </select>
                </div>
                <input 
                  type="text" 
                  placeholder="Years of Experience" 
                  className="w-full p-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-c9-orange focus:border-transparent transition-all duration-300"
                />
                <textarea 
                  placeholder="Tell us about your experience and what you're looking for"
                  rows={4}
                  className="w-full p-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-c9-orange focus:border-transparent transition-all duration-300"
                ></textarea>
                <button type="submit" className="w-full bg-gradient-to-r from-c9-orange to-c9-orange-alt text-white py-4 px-8 rounded-xl font-bold text-lg hover:from-c9-orange-alt hover:to-c9-orange transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
                  Submit Application
                  <ArrowRight className="ml-2 h-5 w-5 inline" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-gradient-to-b from-white to-gray-50">
        <div className="container-full">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-black text-c9-blue mb-6 animate-fade-in-up">
              Success <span className="bg-gradient-to-r from-c9-orange to-c9-orange-alt bg-clip-text text-transparent">Stories</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up delay-100">
              Hear from candidates who have found their perfect roles through C9 Recruitment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="group bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 p-8 text-center transform hover:-translate-y-2 animate-fade-in-up">
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-c9-orange to-c9-orange-alt rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <span className="text-xl font-black text-white">MP</span>
              </div>
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-c9-orange fill-current" />
                ))}
              </div>
              <p className="text-gray-600 mb-6 italic leading-relaxed">
                "C9 found me the perfect driving job within days. The support has been excellent throughout."
              </p>
              <div className="font-bold text-c9-blue text-lg">Michael Parker</div>
              <div className="text-sm text-gray-500">HGV Driver</div>
            </div>
            <div className="group bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 p-8 text-center transform hover:-translate-y-2 animate-fade-in-up delay-100">
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-c9-orange to-c9-orange-alt rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <span className="text-xl font-black text-white">SJ</span>
              </div>
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-c9-orange fill-current" />
                ))}
              </div>
              <p className="text-gray-600 mb-6 italic leading-relaxed">
                "Professional service and they really care about finding the right fit. Highly recommended."
              </p>
              <div className="font-bold text-c9-blue text-lg">Sarah Johnson</div>
              <div className="text-sm text-gray-500">Transport Engineer</div>
            </div>
            <div className="group bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 p-8 text-center transform hover:-translate-y-2 animate-fade-in-up delay-200">
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-c9-orange to-c9-orange-alt rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <span className="text-xl font-black text-white">DW</span>
              </div>
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-c9-orange fill-current" />
                ))}
              </div>
              <p className="text-gray-600 mb-6 italic leading-relaxed">
                "Best agency I've worked with. Great communication and always available when needed."
              </p>
              <div className="font-bold text-c9-blue text-lg">David Williams</div>
              <div className="text-sm text-gray-500">PSV Driver</div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="section-padding bg-gradient-to-br from-c9-blue to-c9-blue-alt text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent"></div>
        <div className="container-full relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-black mb-6 animate-fade-in-up">
              Ready to Take the Next Step?
            </h2>
            <p className="text-xl text-white/90 mb-8 animate-fade-in-up delay-100">
              Our expert recruitment consultants are here to help you find your perfect transport role.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-200">
              <a 
                href="tel:02081524574" 
                className="btn-primary bg-gradient-to-r from-c9-orange to-c9-orange-alt hover:from-c9-orange-alt hover:to-c9-orange transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Call Us Now: 0208 152 4574
              </a>
              <a 
                href="mailto:contact@c9-recruitment.com" 
                className="btn-secondary bg-white/10 backdrop-blur-sm border-2 border-white/20 text-white hover:bg-white hover:text-c9-blue transform hover:scale-105 transition-all duration-300"
              >
                Email Us
              </a>
            </div>
            <div className="mt-8 text-white/80 animate-fade-in-up delay-300">
              <p className="flex items-center justify-center gap-2">
                <MapPin className="h-5 w-5 text-c9-orange" />
                Albany House, 162-168 High Street, Waltham Cross, EN8 7DF
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
